import { getThemeToken } from "../theme/runtimeTheme";
import { distanceKmFromCenter, getBoundsForRadiusKm } from "./mapV2Distance";

/** Hide map labels when the visible viewport spans more than this from search center. */
export const MAP_V2_LABEL_RADIUS_KM = 15;

/** Default fit zoom band after framing project pins. */
export const MAP_V2_FIT_MAX_ZOOM = 14;
export const MAP_V2_FIT_MIN_ZOOM = 11;

const MAP_V2_CANVAS = [
  { elementType: "geometry", stylers: [{ color: "#e8ecef" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#dfe3e6" }] },
  { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.neighborhood", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#e2e6ea" }] },
  { featureType: "landscape", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.business", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#cfd6db" }] },
  { featureType: "road", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "road.local", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#f4f6f8" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#b8c0c8" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#c5d0d8" }] },
];

const MAP_V2_ALL_LABELS_OFF = [
  { elementType: "labels", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text", stylers: [{ visibility: "off" }] },
];

/** Road map with no place/road names (viewport wider than 15 km). */
export const MAP_V2_GOOGLE_STYLES_NONE = [...MAP_V2_CANVAS, ...MAP_V2_ALL_LABELS_OFF];

/**
 * ~50% label density within 15 km of search — area names + highways.
 * (No neighborhood, local streets, POI, or water names.)
 */
export const MAP_V2_GOOGLE_STYLES_NEAR = [
  ...MAP_V2_CANVAS,
  { featureType: "administrative.locality", elementType: "labels", stylers: [{ visibility: "on" }] },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#475569" }],
  },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#e8ecef" }, { weight: 2.5 }],
  },
  { featureType: "administrative.neighborhood", elementType: "labels", stylers: [{ visibility: "on" }] },
  {
    featureType: "administrative.neighborhood",
    elementType: "labels.text.fill",
    stylers: [{ color: "#64748b" }],
  },
  {
    featureType: "administrative.neighborhood",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#e8ecef" }, { weight: 2 }],
  },
  { featureType: "road.highway", elementType: "labels", stylers: [{ visibility: "on" }] },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#64748b" }],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#e8ecef" }, { weight: 2.5 }],
  },
  { featureType: "road.arterial", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "labels", stylers: [{ visibility: "off" }] },
];

/** Default road styles (near-view density). */
export const MAP_V2_GOOGLE_STYLES = MAP_V2_GOOGLE_STYLES_NEAR;

/** Furthest visible edge distance from center, in km. */
export function getVisibleRadiusKmFromMap(map, center) {
  if (!map || !center || typeof map.getBounds !== "function") return Infinity;
  const bounds = map.getBounds();
  if (!bounds) return Infinity;
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const corners = [
    { lat: ne.lat(), lng: ne.lng() },
    { lat: ne.lat(), lng: sw.lng() },
    { lat: sw.lat(), lng: sw.lng() },
    { lat: sw.lat(), lng: ne.lng() },
  ];
  return corners.reduce((max, pt) => {
    const km = distanceKmFromCenter(center, pt);
    return km == null ? max : Math.max(max, km);
  }, 0);
}

/** True when every corner of the visible map is within radiusKm of center. */
export function isViewportWithinRadiusOfCenter(map, center, radiusKm = MAP_V2_LABEL_RADIUS_KM) {
  if (!map || !center) return false;
  const visibleKm = getVisibleRadiusKmFromMap(map, center);
  return Number.isFinite(visibleKm) && visibleKm <= radiusKm + 0.35;
}

/** Pick road-map styles — names only while the viewport stays inside the location radius. */
export function getMapV2StylesForView({ zoom, searchCenter, map }) {
  if (typeof zoom === "number" && zoom < 10) {
    return MAP_V2_GOOGLE_STYLES_NONE;
  }

  if (!searchCenter) {
    return MAP_V2_GOOGLE_STYLES_NEAR;
  }

  if (map && !isViewportWithinRadiusOfCenter(map, searchCenter)) {
    return MAP_V2_GOOGLE_STYLES_NONE;
  }

  return MAP_V2_GOOGLE_STYLES_NEAR;
}

/** @deprecated Use getMapV2StylesForView */
export function getMapV2StylesForLabelRadius(visibleRadiusKm) {
  if (!Number.isFinite(visibleRadiusKm) || visibleRadiusKm > MAP_V2_LABEL_RADIUS_KM) {
    return MAP_V2_GOOGLE_STYLES_NONE;
  }
  return MAP_V2_GOOGLE_STYLES_NEAR;
}

export function applyMapV2RoadLabels(map, { center, mapView = "road" } = {}) {
  if (!map || typeof map.setOptions !== "function") return;
  if (mapView === "satellite") {
    map.setOptions({ styles: [] });
    return;
  }
  const zoom = typeof map.getZoom === "function" ? map.getZoom() : null;
  map.setOptions({
    styles: getMapV2StylesForView({ zoom, searchCenter: center, map }),
  });
}

/** Soft pan limit around search center (non-strict — does not fight fit-all-markers zoom). Returns cleanup. */
export function setupLocationRadiusLock(map, center, radiusKm, googleMaps) {
  if (!map || !center || !googleMaps?.maps) return () => {};

  const bounds = getBoundsForRadiusKm(center, radiusKm, googleMaps);
  if (!bounds) return () => {};

  map.setOptions({
    restriction: { latLngBounds: bounds, strictBounds: false },
  });

  return () => {
    map.setOptions({
      restriction: null,
      minZoom: undefined,
      maxZoom: undefined,
    });
  };
}

/** After fitBounds — zoom in to the radius edge, then lock that as minimum zoom (max zoom-out). */
export function finalizeLocationRadiusMinZoom(
  map,
  center,
  radiusKm = MAP_V2_LABEL_RADIUS_KM,
  googleMaps,
  maxSteps = 12
) {
  if (!map || !center || !googleMaps?.maps?.event) return;

  const finish = () => {
    const z = map.getZoom();
    if (typeof z === "number" && Number.isFinite(z)) {
      map.setOptions({ minZoom: z, maxZoom: 20 });
    }
  };

  const tune = (remaining) => {
    if (isViewportWithinRadiusOfCenter(map, center, radiusKm + 0.25)) {
      finish();
      return;
    }
    if (remaining <= 0) {
      finish();
      return;
    }
    const z = map.getZoom();
    if (typeof z !== "number" || z >= 19) {
      finish();
      return;
    }
    map.setZoom(z + 1);
    googleMaps.maps.event.addListenerOnce(map, "idle", () => tune(remaining - 1));
  };

  tune(maxSteps);
}

export function clampMapFitZoom(map, { minZoom = MAP_V2_FIT_MIN_ZOOM, maxZoom = MAP_V2_FIT_MAX_ZOOM } = {}) {
  if (!map || typeof map.getZoom !== "function" || typeof map.setZoom !== "function") return;
  const z = map.getZoom();
  if (typeof z !== "number" || !Number.isFinite(z)) return;
  if (z > maxZoom) map.setZoom(maxZoom);
  else if (z < minZoom) map.setZoom(minZoom);
}

const DEFAULT_PIN_PALETTE = ["#5BA8FF", "#7DD49A", "#F4A05B", "#C28BE6"];

export function getMapV2PinPalette() {
  return [
    getThemeToken("map_v2_pin_1") || DEFAULT_PIN_PALETTE[0],
    getThemeToken("map_v2_pin_2") || DEFAULT_PIN_PALETTE[1],
    getThemeToken("map_v2_pin_3") || DEFAULT_PIN_PALETTE[2],
    getThemeToken("map_v2_pin_4") || DEFAULT_PIN_PALETTE[3],
  ];
}

export function getMapV2PinColor(index, isSelected) {
  if (isSelected) return getThemeToken("primary") || getThemeToken("cta_primary") || "#ea580c";
  return getThemeToken("map_v2_pin_idle") || "#64748b";
}

/** Locality boundary stroke — dotted red like map reference. */
export function getMapV2BoundaryColor() {
  return getThemeToken("map_v2_boundary") || "#ef4444";
}

/** Neutral stroke/fill for the active distance ring (single shade). */
export function getMapV2RingColor() {
  return getThemeToken("map_v2_ring") || "#94a3b8";
}

export function getMapV2CanvasBg() {
  return getThemeToken("map_v2_canvas_bg") || "#E8ECEF";
}
